/**
 * S5 — pure helpers for segment billing on the Scheduling Board drawer and billing pages (Final PRD §35–47, §48, §59). No API access.
 */
export const DISPOSITION_LABEL = { BILLABLE: 'Billable', NON_BILLABLE: 'Non-billable' };

export const NON_BILLABLE_REASONS = [
  ['SERVICE_NOT_DELIVERED', 'Service not delivered'], ['PARTICIPANT_ABSENT', 'Participant absent'], ['PROVIDER_DECISION', 'Provider decision'],
  ['CANCELLATION_NOT_CLAIMABLE', 'Cancellation not claimable'], ['DUPLICATE_OR_ERROR', 'Duplicate / error'], ['OUTSIDE_AUTHORITY', 'Outside agreement authority'],
  ['INSUFFICIENT_EVIDENCE', 'Insufficient evidence'], ['OTHER', 'Other (comment required)'],
];

export const dispositionLabel = (d) => (d ? DISPOSITION_LABEL[d] || d : 'Not yet assessed');

/** Validation mirror of the server rule (§35): NON_BILLABLE needs a reason; OTHER needs a comment. */
export const dispositionProblem = (disposition, reasonCode, comment) => {
  if (!disposition) return 'Choose Billable or Non-billable';
  if (disposition === 'NON_BILLABLE' && !reasonCode) return 'A reason is required for Non-billable';
  if (disposition === 'NON_BILLABLE' && reasonCode === 'OTHER' && !(comment || '').trim()) return 'A comment is required when the reason is Other';
  return null;
};

export const STAGE_TONE = { PASS: 'emerald', WARN: 'amber', BLOCK: 'red' };
export const stageTone = (stage) => STAGE_TONE[stage] || 'slate';

/** Business-rule text for readiness codes (§42). Unknown codes fall back to a readable form of the code. */
export const CODE_TEXT = {
  NO_ACTIVE_AGREEMENT: 'No agreement version has billing authority on the service date',
  NO_AGREEMENT_LINE: 'Agreement line invalid or outside its effective dates for the service date',
  PSI_MISSING: 'Participant service item (PSI) missing or invalid',
  LINEAGE_INVALID: 'Line → PSI lineage does not match the segment',
  CATALOGUE_FALLBACK_PROHIBITED: 'Rate would come from the Service Catalogue — prohibited; only the signed agreement line applies',
  SIGNED_RATE_MISSING: 'The signed agreement document carries no rate for this line',
  SIGNED_RATE_MISMATCH: 'Line rate differs from the signed document rate — controlled variation required',
  RATE_TABLE_DATA_MISSING: 'RATE_TABLE line has no signed rate-table rows (no fallback to current pricing)',
  PRICE_LIMIT_EXCEEDED: 'Signed rate exceeds the NDIS price limit on the service date (never reduced automatically)',
  RATE_EXCEEDS_CAP: 'Rate exceeds the NDIS price limit',
  ATTENDANCE_MISSING: 'Participant attendance not recorded — mandatory, never inferred from a clock',
  DELIVERY_EVIDENCE_MISSING: 'Delivery evidence (actual window) missing',
  PENDING_VERIFICATION: 'Attendance or cancellation outcome awaiting approval',
  NON_BILLABLE: 'Not billable to the participant (delivery outcome or cancellation outcome)',
  UNRESOLVED_CLAIM_RULE: 'Claim rule deferred (shared sleep-over) — record is PROVISIONAL, not invoiceable',
  SCOPE_FAILURE: 'Security / organisation scope failure',
  MY_PROVIDER_NOT_CONFIRMED: 'Agency-managed: no confirmed "my provider" relationship (advisory)',
  POSSIBLE_DUPLICATE: 'Another approved record overlaps this service',
  FUNDING_EXCEEDED: 'Amount exceeds tracked funding',
};
export const codeText = (code) => CODE_TEXT[code] || String(code || '').replace(/_/g, ' ').toLowerCase();

/** What the operator may do next from a readiness summary (§43): WARN may be continued with a reason; BLOCK never. */
export const readinessAction = (summary, disposition) => {
  if (disposition !== 'BILLABLE') return 'NONE';
  if (!summary?.stage) return 'EVALUATE';
  if (summary.stage === 'BLOCK') return 'BLOCKED';
  if (summary.stage === 'WARN') {
    const cont = summary.warn_continuation;
    const covered = cont && (summary.warning_codes || []).every((c) => (cont.codes || []).includes(c));
    return covered ? 'RECORD' : 'CONTINUE_WARN';
  }
  return 'RECORD';
};

/** Human chain for a billing-record lineage (§46), most upstream last. */
export const lineageSteps = (lineage) => {
  if (!lineage) return [];
  const sv = lineage.signed_agreement_version || {};
  return [
    ['Segment', lineage.participant_service_segment_id], ['Participant shift', lineage.participant_shift_id], ['PSI', lineage.participant_service_item_id],
    ['Agreement line', lineage.agreement_line_id], ['Signed agreement version', sv.agreement_no ? `${sv.agreement_no} v${sv.version_no}${sv.signed_at ? ` (signed ${String(sv.signed_at).slice(0, 10)})` : ''}` : sv.agreement_id],
  ].filter(([, v]) => v);
};
